import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { SubscriptionPlansCarousel } from '@/modules/portal/components/subscription-plans/SubscriptionPlansCarousel';
import { TrialStatusBadge } from '@/modules/monitor/components/TrialStatusBadge';
import {
  PortalAutoRenewBadge,
  PortalDaysRemainingDisplay,
  PortalEmptyValue,
  PortalSubscriptionStatusBadge,
  PortalSystemAccessBadge,
} from '@/modules/portal/components/PortalStatusBadges';
import { PortalDetailGrid } from '@/modules/portal/components/ui/PortalDetailGrid';
import { PortalKpiCard } from '@/modules/portal/components/ui/PortalKpiCard';
import { PortalPage } from '@/modules/portal/components/ui/PortalPage';
import { PortalProgress } from '@/modules/portal/components/ui/PortalProgress';
import { PortalQuickActionCard } from '@/modules/portal/components/ui/PortalQuickActionCard';
import {
  PortalTimeline,
  type PortalTimelineItem,
} from '@/modules/portal/components/ui/PortalTimeline';
import { PortalLoadingState, portalSpinClass } from '@/modules/portal/components/ui/portal-loading';
import { PortalStagger } from '@/modules/portal/components/ui/portal-motion';
import { fetchPortalOverview } from '@/modules/portal/services/portalOverview.api';
import type { PortalOverview } from '@/modules/portal/types/portalOverview';
import {
  formatLatestPaymentSummary,
  formatPortalDate,
  portalDaysBadgeKey,
  portalPeriodProgressPercent,
} from '@/modules/portal/utils/portalOverviewHelpers';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

function buildSubscriptionTimeline(
  data: PortalOverview,
  locale: string,
  t: TFunction,
): PortalTimelineItem[] {
  const periodEndLabel =
    data.subscription_status === 'trialing' && data.trial_ends_at
      ? t('portal.subscription.fields.trialEnd')
      : t('portal.subscription.fields.periodEnd');
  const periodEndValue =
    data.subscription_status === 'trialing' && data.trial_ends_at
      ? formatPortalDate(data.trial_ends_at, locale)
      : formatPortalDate(data.current_period_end, locale);

  const items: PortalTimelineItem[] = [];

  if (data.started_at) {
    items.push({
      id: 'started',
      title: t('portal.subscription.timeline.started'),
      subtitle: formatPortalDate(data.started_at, locale),
      status: 'complete',
    });
  }

  if (data.current_period_start) {
    items.push({
      id: 'period-start',
      title: t('portal.subscription.timeline.periodStart'),
      subtitle: formatPortalDate(data.current_period_start, locale),
      status: 'complete',
    });
  }

  if (periodEndValue !== '—') {
    items.push({
      id: 'period-end',
      title: periodEndLabel,
      subtitle: periodEndValue,
      status: data.days_remaining !== null && data.days_remaining > 0 ? 'upcoming' : 'current',
      meta:
        data.days_remaining !== null && data.days_remaining >= 0 ? (
          <span className="text-xs font-medium text-muted-foreground">
            {t('trials.daysRemaining.left', { count: data.days_remaining })}
          </span>
        ) : undefined,
    });
  }

  if (data.subscription_status) {
    items.push({
      id: 'status',
      title: t('portal.subscription.timeline.current'),
      subtitle: t(`subscriptions.status.${data.subscription_status}`, {
        defaultValue: data.subscription_status,
      }),
      status: 'current',
      meta: <PortalSubscriptionStatusBadge status={data.subscription_status} />,
    });
  }

  return items;
}

export function PortalSubscriptionPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US';

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['me', 'portal-overview'],
    queryFn: fetchPortalOverview,
  });

  const formatPaymentStatus = (status: string) =>
    t(`payments.status.${status}`, { defaultValue: status });

  if (isLoading) {
    return (
      <PortalLoadingState
        size="md"
        message={t('common.loading')}
        className="min-h-[280px] justify-center"
      />
    );
  }

  if (error) {
    return (
      <div className="portal-glass-panel mx-auto max-w-lg space-y-4 rounded-2xl border-destructive/30 p-6">
        <h2 className="text-lg font-semibold text-foreground">
          {t('portal.subscription.error.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('portal.subscription.error.message')}</p>
        <Button type="button" variant="outline" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const hasSubscription = data.subscription_id !== null;
  const paymentSummary = formatLatestPaymentSummary(data, locale, formatPaymentStatus);
  const periodProgress = portalPeriodProgressPercent(data);
  const daysKey = portalDaysBadgeKey(data);
  const isUrgent =
    data.days_remaining !== null &&
    data.days_remaining <= 7 &&
    (data.subscription_status === 'trialing' || data.subscription_status === 'active');

  const periodEndLabel =
    data.subscription_status === 'trialing' && data.trial_ends_at
      ? t('portal.subscription.fields.trialEnd')
      : t('portal.subscription.fields.periodEnd');

  const periodEndValue =
    data.subscription_status === 'trialing' && data.trial_ends_at
      ? formatPortalDate(data.trial_ends_at, locale)
      : formatPortalDate(data.current_period_end, locale);

  const timelineItems = buildSubscriptionTimeline(data, locale, t);

  const subscriptionDetailItems = [
    {
      id: 'company',
      label: t('portal.subscription.fields.company'),
      value: data.tenant_name || <PortalEmptyValue />,
    },
    {
      id: 'admin',
      label: t('portal.subscription.fields.admin'),
      value: data.customer_name ? (
        <span>
          {data.customer_name}
          <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
            {data.customer_email}
          </span>
        </span>
      ) : (
        data.customer_email || <PortalEmptyValue />
      ),
    },
    {
      id: 'product',
      label: t('portal.subscription.fields.product'),
      value: data.product_name ?? <PortalEmptyValue />,
    },
    {
      id: 'plan',
      label: t('portal.subscription.fields.plan'),
      value: data.plan_name ?? <PortalEmptyValue />,
    },
    {
      id: 'status',
      label: t('portal.subscription.fields.subscriptionStatus'),
      value: <PortalSubscriptionStatusBadge status={data.subscription_status} />,
    },
    {
      id: 'trial',
      label: t('portal.subscription.fields.trialStatus'),
      value: data.trial_status ? (
        <TrialStatusBadge
          trialStatus={data.trial_status}
          subscriptionStatus={data.subscription_status ?? ''}
        />
      ) : (
        <PortalEmptyValue />
      ),
    },
    {
      id: 'start',
      label: t('portal.subscription.fields.startDate'),
      value: formatPortalDate(data.started_at, locale),
    },
    {
      id: 'period-start',
      label: t('portal.subscription.fields.periodStart'),
      value: formatPortalDate(data.current_period_start, locale),
    },
    {
      id: 'period-end',
      label: periodEndLabel,
      value: periodEndValue,
    },
    ...(data.subscription_status !== 'trialing'
      ? [
          {
            id: 'trial-end',
            label: t('portal.subscription.fields.trialEnd'),
            value:
              data.trial_ends_at !== null ? (
                formatPortalDate(data.trial_ends_at, locale)
              ) : (
                <PortalEmptyValue />
              ),
          },
        ]
      : []),
    {
      id: 'remaining',
      label: t('portal.subscription.fields.remainingDays'),
      value: <PortalDaysRemainingDisplay overview={data} />,
    },
  ];

  const accessItems = [
    {
      id: 'entitlement',
      label: t('portal.subscription.fields.entitlement'),
      value: data.entitlement_status ? (
        t(`portal.overview.entitlement.${data.entitlement_status}`, {
          defaultValue: data.entitlement_status,
        })
      ) : (
        <PortalEmptyValue />
      ),
    },
    {
      id: 'system-access',
      label: t('portal.subscription.fields.systemAccess'),
      value: <PortalSystemAccessBadge status={data.system_access_status} />,
    },
  ];

  const billingItems = [
    {
      id: 'payment',
      label: t('portal.subscription.fields.latestPayment'),
      value: paymentSummary ?? <PortalEmptyValue />,
    },
    {
      id: 'payment-date',
      label: t('portal.subscription.fields.latestPaymentDate'),
      value: formatPortalDate(data.last_payment_at, locale),
    },
    {
      id: 'auto-renew',
      label: t('portal.subscription.fields.autoRenew'),
      value: <PortalAutoRenewBadge autoRenew={data.auto_renew} />,
    },
  ];

  return (
    <PortalPage
      title={t('portal.pages.subscription.title')}
      description={t('portal.subscription.description')}
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isFetching}
          onClick={() => void refetch()}
          className="rounded-xl"
        >
          <Icons.RefreshCw className={cn('me-2 h-4 w-4', isFetching && portalSpinClass)} />
          {t('common.refresh')}
        </Button>
      }
    >
      <PortalStagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <PortalKpiCard
          label={t('portal.subscription.cards.subscriptionStatus')}
          icon={Icons.Repeat}
        >
          <PortalSubscriptionStatusBadge status={data.subscription_status} />
        </PortalKpiCard>
        <PortalKpiCard
          label={t('portal.subscription.cards.remainingDays')}
          icon={Icons.Timer}
          highlight={isUrgent}
        >
          <PortalDaysRemainingDisplay overview={data} />
        </PortalKpiCard>
        <PortalKpiCard label={t('portal.subscription.cards.systemAccess')} icon={Icons.CheckCircle}>
          <PortalSystemAccessBadge status={data.system_access_status} />
        </PortalKpiCard>
        <PortalKpiCard label={t('portal.subscription.cards.latestPayment')} icon={Icons.DollarSign}>
          {paymentSummary ? (
            <p className="text-sm font-medium leading-snug text-foreground">{paymentSummary}</p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('portal.overview.noPayment')}</p>
          )}
          {data.has_payment_issue ? (
            <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-400">
              {t('portal.overview.paymentIssue')}
            </p>
          ) : null}
        </PortalKpiCard>
      </PortalStagger>

      {hasSubscription && periodProgress !== null && daysKey === 'left' ? (
        <PortalProgress
          value={periodProgress}
          label={t('portal.subscription.periodProgress')}
          hint={t('portal.subscription.periodProgressHint', { percent: periodProgress })}
          urgent={isUrgent}
        />
      ) : null}

      {!hasSubscription ? (
        <div className="portal-empty-state">
          <Icons.Layers className="mx-auto h-10 w-10 text-muted-foreground/50" aria-hidden />
          <p className="mt-4 text-lg font-semibold text-foreground">
            {t('portal.subscription.empty.title')}
          </p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {t('portal.subscription.empty.message')}
          </p>
        </div>
      ) : (
        <>
          {timelineItems.length > 0 ? (
            <PortalTimeline title={t('portal.subscription.timeline.title')} items={timelineItems} />
          ) : null}

          <SubscriptionPlansCarousel enabled={hasSubscription} />

          <PortalDetailGrid
            title={t('portal.subscription.sections.details')}
            description={t('portal.subscription.sections.detailsHint')}
            items={subscriptionDetailItems}
          />

          <PortalDetailGrid
            title={t('portal.subscription.sections.access')}
            description={t('portal.subscription.sections.accessHint')}
            items={accessItems}
          />

          <PortalDetailGrid
            title={t('portal.subscription.sections.billing')}
            description={t('portal.subscription.sections.billingHint')}
            items={billingItems}
          />
        </>
      )}

      <section>
        <h2 className="text-base font-semibold text-foreground">
          {t('portal.subscription.quickActions')}
        </h2>
        <PortalStagger className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <PortalQuickActionCard
            to="/portal/payments"
            label={t('portal.subscription.actions.viewPayments')}
            description={t('portal.subscription.actions.viewPaymentsHint')}
            icon={Icons.DollarSign}
          />
          <PortalQuickActionCard
            to="/portal/renew"
            label={t('portal.subscription.actions.renew')}
            description={t('portal.subscription.actions.renewHint')}
            icon={Icons.RefreshCw}
          />
          <PortalQuickActionCard
            to="/portal/upgrade"
            label={t('portal.subscription.actions.upgrade')}
            description={t('portal.subscription.actions.upgradeHint')}
            icon={Icons.Layers}
          />
        </PortalStagger>
      </section>
    </PortalPage>
  );
}
